import { useFormContext, useWatch } from "react-hook-form";

import { Card } from "src/base-components/Card";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { EditorAccordionItem as AccordionItem } from "src/base-components/EditorAccordionItem";
import { Switch } from "src/base-components/Switch";
import { CaseTemplateFormValues } from "src/cases/templates/caseTemplateFormUtils";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { useModal } from "src/design-system/Modal";

type FormToggleAccordionProps = {
  itemIndex: number;
  itemType: "task" | "action";
  accordionValue: string;
  children: React.ReactNode;
  onEnableAccordionItem: (value: string) => void;
  onDisableAccordionItem: (value: string) => void;
};

export const FormToggleAccordion: React.FC<FormToggleAccordionProps> = ({
  itemIndex,
  itemType,
  accordionValue,
  children,
  onEnableAccordionItem,
  onDisableAccordionItem,
}) => {
  const form = useFormContext<CaseTemplateFormValues>();
  const hasPermission = useCheckFieldPermission();

  const fieldPath = itemType === "task" ? "case_tasks" : "case_actions";
  const isFormEnabled = useWatch({
    name: `attributes.${fieldPath}.${itemIndex}.form`,
    compute: (value) => Array.isArray(value?.fields),
  });

  const {
    openModal: openFormDeleteModal,
    isOpen: isFormDeleteModalOpen,
    closeModal: closeFormDeleteModal,
  } = useModal();

  const toggleOffForm = () => {
    form.setValue(`attributes.${fieldPath}.${itemIndex}.form`, null);
    closeFormDeleteModal();
    onDisableAccordionItem(accordionValue);
  };

  const toggleOnForm = () => {
    form.setValue(`attributes.${fieldPath}.${itemIndex}.form`, {
      fields: [
        {
          type: "text" as const,
          key: "",
          display_name: "",
          required: false,
          description: "",
        },
      ],
    });
  };

  const onToggleForm = () => {
    if (isFormEnabled) {
      openFormDeleteModal();
    } else {
      onEnableAccordionItem(accordionValue);
      toggleOnForm();
    }
  };

  return (
    <>
      <AccordionItem
        className="p-3 pt-0"
        dataLoc="form-builder-accordion"
        disabled={!isFormEnabled}
        headerContent={
          <span className="mr-4">
            <Switch
              dataLoc="form-enabled-switch"
              disabled={
                !hasPermission(
                  "edit",
                  `attributes.${fieldPath}.${itemIndex}.form`,
                )
              }
              enabled={isFormEnabled}
              onChange={onToggleForm}
            />
          </span>
        }
        title="Configure the fields the reviewer should fill out"
        value={accordionValue}
      >
        {isFormEnabled && <Card className="my-2 w-full">{children}</Card>}
      </AccordionItem>
      <ConfirmationModal
        cancelationButtonText="Cancel"
        confirmationButtonText="Delete"
        description="Are you sure you want to delete this form group? This action cannot be undone. All forms inside this group will be removed permanently."
        open={isFormDeleteModalOpen}
        title="Delete form group"
        variant="danger"
        onClose={closeFormDeleteModal}
        onConfirm={toggleOffForm}
      />
    </>
  );
};
